import { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { FiMenu, FiPlus, FiSearch, FiCalendar, FiDollarSign, FiFilter, FiChevronDown, FiChevronLeft, FiChevronRight, FiInfo, FiX } from 'react-icons/fi';
import { CreditCard, Phone } from 'lucide-react';
import { format, startOfDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { DateRangePicker } from './ui/DateRangePicker';
import { CustomSelect } from './ui/CustomSelect';
import { cpApi } from '../api/api';
import { useTransactionsFilters } from '../context/TransactionsFilterContext';
import { useDashboardFilter } from '../context/useDashboardFilter';

const PICK_DIRECTIONS = [
  { value: 'tbank_c2c', label: 'TBank C2C' },
  { value: 'tbank_sbp', label: 'TBank СБП' },
  { value: 'interbank_c2c', label: 'Interbank C2C' },
  { value: 'interbank_sbp', label: 'Interbank СБП' },
];

const HOLD_SECONDS_HELP =
  '0 — один запрос к списку кандидатов.\n\n1–300 — повтор поиска каждые 2 с, пока не появятся заявки или не истечёт выдержка (не больше 300 с).';

type MatchCandidate = {
  id: number;
  order_id: string;
  amount: number;
  payed_amount?: number;
  payed_amount_counterparty?: number;
  remaining_rub: number;
  bank_name?: string;
  direction_code?: string | null;
  card_holder?: string;
  exchange_rate?: number;
  status?: string;
  deadline_at?: string | null;
  max_matches?: number;
};

function matchCandidateToTakeRequest(c: MatchCandidate) {
  return {
    id: c.id,
    order_id: c.order_id,
    amount: c.amount,
    payed_amount: c.payed_amount ?? 0,
    payed_amount_counterparty: c.payed_amount_counterparty ?? 0,
    bank_name: c.bank_name,
    direction_code: c.direction_code ?? undefined,
    card_holder: c.card_holder,
    exchange_rate: c.exchange_rate,
    status: c.status || 'work',
    deadline_at: c.deadline_at ?? null,
    max_matches: c.max_matches,
  };
}

interface Props {
  onMenuToggle: () => void;
  isRequestsPage?: boolean;
  isSettingsPage?: boolean;
  showAirexRequestsFilters?: boolean;
  showTeamSearch?: boolean;
  teamSearchQuery?: string;
  onTeamSearchChange?: (query: string) => void;
  showAddTeamMemberButton?: boolean;
  onAddTeamMember?: () => void;
  showNewTransactionButton?: boolean;
  onNewTransaction?: () => void;
  statusFilter?: string;
  onStatusFilterChange?: (status: string) => void;
  dateRange?: { from: Date | null; to: Date | null };
  onDateRangeChange?: (range: { from: Date | null; to: Date | null }) => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  onRefresh?: () => void;
  showTransactionsFilters?: boolean;
  showDashboardDateFilter?: boolean;
  showWithdrawButton?: boolean;
  onWithdraw?: () => void;
}

export default function CounterpartyHeader({
  onMenuToggle,
  isSettingsPage,
  showAirexRequestsFilters,
  showTeamSearch,
  teamSearchQuery = '',
  onTeamSearchChange,
  showAddTeamMemberButton,
  onAddTeamMember,
  showNewTransactionButton,
  onNewTransaction,
  dateRange,
  onDateRangeChange,
  searchQuery = '',
  onSearchChange,
  onRefresh,
  showTransactionsFilters,
  showDashboardDateFilter,
  showWithdrawButton,
  onWithdraw,
}: Props) {
  const txFilters = useTransactionsFilters();
  const dashboardFilter = useDashboardFilter();
  const [moscowTime, setMoscowTime] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTxDatePicker, setShowTxDatePicker] = useState(false);
  const [showDashboardDatePicker, setShowDashboardDatePicker] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [dashboardPickerValue, setDashboardPickerValue] = useState<{ from: Date | null; to: Date | null }>({ from: null, to: null });
  const headerRef = useRef<HTMLElement | null>(null);

  const [isTableUpdating, setIsTableUpdating] = useState(false);
  const [pickWidgetOpen, setPickWidgetOpen] = useState(false);
  const [pickAmount, setPickAmount] = useState('');
  const [pickDirection, setPickDirection] = useState('');
  const [pickBankName, setPickBankName] = useState('');
  const [pickBankOptions, setPickBankOptions] = useState<string[]>([]);
  const [pickFilterTab, setPickFilterTab] = useState<'direction' | 'bank'>('direction');
  const [pickDropdownOpen, setPickDropdownOpen] = useState(false);
  /** Выдержка (сек): при >0 повторять postMatchCandidates каждые 2 с в течение указанного времени */
  const [pickTimeoutSeconds, setPickTimeoutSeconds] = useState('0');
  const pickSearchGenRef = useRef(0);
  const [isSearchingMatches, setIsSearchingMatches] = useState(false);
  const [matchCandidates, setMatchCandidates] = useState<MatchCandidate[]>([]);
  const [carouselIdx, setCarouselIdx] = useState(0);
  const [cpSettings, setCpSettings] = useState<{ allowed_directions?: string[]; max_amount_limit?: number | null }>({});

  const resetMatchCarousel = () => {
    setMatchCandidates([]);
    setCarouselIdx(0);
  };

  const pickWidgetWasOpenRef = useRef(false);

  useEffect(() => {
    if (pickWidgetWasOpenRef.current && !pickWidgetOpen) {
      pickSearchGenRef.current += 1;
      setIsSearchingMatches(false);
    }
    pickWidgetWasOpenRef.current = pickWidgetOpen;
  }, [pickWidgetOpen]);

  const pickDirectionOptions = useMemo(() => {
    return cpSettings.allowed_directions?.length
      ? PICK_DIRECTIONS.filter((item) => cpSettings.allowed_directions!.includes(item.value))
      : PICK_DIRECTIONS;
  }, [cpSettings.allowed_directions]);
  const selectedDirectionLabel = pickDirectionOptions.find((item) => item.value === pickDirection)?.label ?? 'Любое';
  const selectedPickTargetLabel =
    pickDirection !== ''
      ? `Направл.: ${selectedDirectionLabel}`
      : pickBankName !== ''
        ? `Банк: ${pickBankName}`
        : 'Любое';

  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const setVar = () => {
      const h = el.getBoundingClientRect().height;
      if (Number.isFinite(h) && h > 0) {
        document.documentElement.style.setProperty('--airex-header-height', `${Math.round(h)}px`);
      }
    };
    setVar();
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(setVar);
      ro.observe(el);
    } else {
      window.addEventListener('resize', setVar);
    }
    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', setVar);
    };
  }, []);

  useEffect(() => {
    const update = () => {
      setMoscowTime(
        formatInTimeZone(new Date(), 'Europe/Moscow', 'HH:mm:ss'),
      );
    };
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!showDatePicker) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const picker = document.querySelector('[data-date-range-picker]');
      const btn = target.closest('[data-date-picker-button]');
      if (picker && !picker.contains(target) && !btn) setShowDatePicker(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDatePicker]);

  useEffect(() => {
    if (!showTxDatePicker) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const picker = document.querySelector('[data-tx-date-range-picker]');
      const btn = target.closest('[data-tx-date-picker-button]');
      if (picker && !picker.contains(target) && !btn) setShowTxDatePicker(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTxDatePicker]);

  useEffect(() => {
    if (!showDashboardDatePicker) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const picker = document.querySelector('[data-dashboard-date-range-picker]');
      const btn = target.closest('[data-dashboard-date-picker-button]');
      if (picker && !picker.contains(target) && !btn) {
        setShowDashboardDatePicker(false);
        setDashboardPickerValue({ from: null, to: null });
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDashboardDatePicker]);

  useEffect(() => {
    if (showAirexRequestsFilters && pickWidgetOpen) {
      Promise.all([
        cpApi.getProfile(),
        cpApi.getBankNamesMappings().catch(() => []),
      ]).then(([cp, bankMappings]) => {
        setCpSettings({
          allowed_directions: cp.allowed_directions || [],
          max_amount_limit: cp.max_amount_limit ?? null,
        });
        const banks = Array.isArray(bankMappings)
          ? bankMappings
            .map((m) => String(m?.canonical_name || '').trim())
            .filter((name): name is string => Boolean(name))
          : [];
        setPickBankOptions(Array.from(new Set(banks)));
      }).catch(() => {});
    }
  }, [showAirexRequestsFilters, pickWidgetOpen]);

  useEffect(() => {
    if (!showAirexRequestsFilters) return;
    (window as unknown as { setAirexTableUpdating?: (v: boolean) => void }).setAirexTableUpdating = setIsTableUpdating;
    return () => {
      delete (window as unknown as { setAirexTableUpdating?: (v: boolean) => void }).setAirexTableUpdating;
    };
  }, [showAirexRequestsFilters]);

  useEffect(() => {
    if (!showAirexRequestsFilters || !pickWidgetOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const widget = document.getElementById('airex-pick-widget-cp');
      const btn = target.closest('[data-airex-pick-widget-button]');
      const pickDropdown = target.closest('[data-pick-target-dropdown]');
      if (widget && !widget.contains(target) && !btn) {
        setPickWidgetOpen(false);
        setPickDropdownOpen(false);
      } else if (!pickDropdown) {
        setPickDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAirexRequestsFilters, pickWidgetOpen]);

  useEffect(() => {
    if (!pickWidgetOpen) return;
    const mq = window.matchMedia('(max-width: 767px)');
    const sync = () => {
      if (mq.matches) {
        const prev = document.body.style.overflow;
        document.body.dataset.cpPickPrevOverflow = prev;
        document.body.style.overflow = 'hidden';
      } else {
        const prev = document.body.dataset.cpPickPrevOverflow;
        if (prev !== undefined) {
          document.body.style.overflow = prev;
          delete document.body.dataset.cpPickPrevOverflow;
        }
      }
    };
    sync();
    mq.addEventListener('change', sync);
    return () => {
      mq.removeEventListener('change', sync);
      const prev = document.body.dataset.cpPickPrevOverflow;
      if (prev !== undefined) {
        document.body.style.overflow = prev;
        delete document.body.dataset.cpPickPrevOverflow;
      } else {
        document.body.style.overflow = '';
      }
    };
  }, [pickWidgetOpen]);

  useEffect(() => {
    if (!pickWidgetOpen) setPickDropdownOpen(false);
  }, [pickWidgetOpen]);

  useEffect(() => {
    setShowMobileFilters(false);
  }, [showAirexRequestsFilters, showTransactionsFilters, showDashboardDateFilter, showTeamSearch]);

  return (
    <header
      ref={headerRef}
      className="bg-gradient-to-r from-blue-900 to-indigo-900 dark:from-blue-950 dark:to-indigo-950 shadow-sm border-b border-white/10 shrink-0"
    >
      <div className="flex items-center justify-between pl-0 pr-3 py-2.5 gap-2 min-h-[64px] md:py-4 md:h-20 flex-shrink-0 flex-wrap md:flex-nowrap">
        <div className="flex items-center gap-2 flex-wrap min-w-0 flex-1">
          <button onClick={onMenuToggle} className="p-2 rounded-lg text-white/70 hover:bg-white/10 lg:hidden">
            <FiMenu className="w-5 h-5" />
          </button>
          {(showAirexRequestsFilters || showTransactionsFilters || showDashboardDateFilter || showTeamSearch) && (
            <button
              type="button"
              onClick={() => setShowMobileFilters((v) => !v)}
              className="md:hidden inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs"
            >
              <FiFilter className="w-3.5 h-3.5" />
              Фильтры
            </button>
          )}

          <div className={`${showMobileFilters ? 'flex' : 'hidden'} md:flex items-start md:items-center gap-2 flex-col md:flex-row md:flex-wrap min-w-0 w-full`}>
            {showTransactionsFilters && txFilters && (
              <>
              <input
                type="text"
                placeholder="Поиск по ID, участнику, WT №..."
                value={txFilters.filters.search}
                onChange={(e) => txFilters.setSearch(e.target.value)}
                className="px-3 py-1.5 text-xs bg-white/10 text-white placeholder-white/50 border-0 rounded-lg focus:ring-2 focus:ring-white/50 focus:outline-none w-full md:w-64 md:min-w-[200px]"
              />
              {txFilters.isResponsible && (
                <CustomSelect
                  value={String(txFilters.filters.memberFilter ?? 'all')}
                  onChange={(value) => txFilters.setMemberFilter(value)}
                  options={[
                    { value: 'all', label: 'Все участники' },
                    ...((txFilters.team?.members ?? []).map((m) => ({
                      value: String(m.counterparty_id),
                      label: m.counterparty_name ?? `ID ${m.counterparty_id}`,
                    }))),
                  ]}
                  className="w-full md:w-36"
                />
              )}
              <CustomSelect
                value={String(txFilters.filters.typeFilter ?? 'all')}
                onChange={(value) => txFilters.setTypeFilter(value)}
                options={[
                  { value: 'all', label: 'Все типы' },
                  { value: 'deposit', label: 'Пополнение' },
                  { value: 'withdrawal', label: 'Изъятие' },
                ]}
                className="w-full md:w-32"
              />
              </>
            )}
            {showTeamSearch && (
              <input
                type="text"
                placeholder="Поиск по имени..."
                value={teamSearchQuery}
                onChange={(e) => onTeamSearchChange?.(e.target.value)}
                className="px-3 py-1 text-xs bg-white/10 text-white placeholder-white/50 border-0 rounded-lg focus:ring-2 focus:ring-white/50 focus:outline-none w-full md:w-64"
              />
            )}
            {showAirexRequestsFilters && (
              <>
              <div className="w-full md:w-auto">
                <input
                  type="text"
                  placeholder="Поиск по ID, карте..."
                  value={searchQuery}
                  onChange={(e) => {
                    const v = e.target.value;
                    onSearchChange?.(v);
                    window.dispatchEvent(new CustomEvent('searchChange', { detail: { search: v } }));
                  }}
                  className="px-3 py-1 text-xs bg-white/10 text-white placeholder-white/50 border-0 rounded-lg focus:ring-2 focus:ring-white/50 focus:outline-none w-full md:w-64"
                />
              </div>
              {txFilters?.isResponsible && txFilters?.team?.members && txFilters.team.members.length > 0 && (
                <div className="w-full md:w-auto">
                  <CustomSelect
                    value={String(txFilters.filters.memberFilter ?? 'all')}
                    onChange={(v) => {
                      txFilters.setMemberFilter(v);
                      window.dispatchEvent(new CustomEvent('airexMemberFilterChange', { detail: { memberId: v } }));
                    }}
                    options={[
                      { value: 'all', label: 'Все' },
                      ...txFilters.team.members.map((m) => ({
                        value: String(m.counterparty_id),
                        label: m.counterparty_name || m.login || `#${m.counterparty_id}`,
                      })),
                    ]}
                    className="w-full md:min-w-[140px]"
                  />
                </div>
              )}
              </>
            )}
          </div>
        </div>

        {(showAirexRequestsFilters || showTransactionsFilters || showDashboardDateFilter || (showTeamSearch && (showAddTeamMemberButton || showNewTransactionButton))) && (
          <div className={`${showMobileFilters ? 'grid grid-cols-2 gap-2' : 'hidden'} md:flex items-center md:gap-2 flex-shrink-0 w-full md:w-auto md:flex-wrap md:flex-nowrap md:justify-end`}>
            {showDashboardDateFilter && dashboardFilter && (
              <div className="relative">
                <button
                  data-dashboard-date-picker-button
                  type="button"
                  onClick={() => {
                    const open = !showDashboardDatePicker;
                    setShowDashboardDatePicker(open);
                    if (open && dashboardFilter.range.from && dashboardFilter.range.to) {
                      setDashboardPickerValue({
                        from: new Date(dashboardFilter.range.from + 'T00:00:00'),
                        to: new Date(dashboardFilter.range.to + 'T23:59:59'),
                      });
                    } else if (!open) {
                      setDashboardPickerValue({ from: null, to: null });
                    }
                  }}
                  className={`flex items-center justify-center w-10 h-10 rounded-lg transition-colors duration-200 ${
                    dashboardFilter.range.from && dashboardFilter.range.to
                      ? 'bg-blue-500 text-white'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                  title="Период дашборда"
                >
                  <FiCalendar className="w-4 h-4" />
                </button>
                {showDashboardDatePicker && (
                  <div data-dashboard-date-range-picker className="absolute right-0 top-12 z-50">
                    <DateRangePicker
                      value={
                        dashboardPickerValue.from !== null || dashboardPickerValue.to !== null
                          ? dashboardPickerValue
                          : {
                              from: dashboardFilter.range.from ? new Date(dashboardFilter.range.from + 'T00:00:00') : null,
                              to: dashboardFilter.range.to ? new Date(dashboardFilter.range.to + 'T23:59:59') : null,
                            }
                      }
                      onChange={(range) => {
                        if (range.from && range.to) {
                          const fromStr = `${range.from.getFullYear()}-${String(range.from.getMonth() + 1).padStart(2, '0')}-${String(range.from.getDate()).padStart(2, '0')}`;
                          const toStr = `${range.to.getFullYear()}-${String(range.to.getMonth() + 1).padStart(2, '0')}-${String(range.to.getDate()).padStart(2, '0')}`;
                          dashboardFilter.setRange(fromStr, toStr);
                          setDashboardPickerValue({ from: null, to: null });
                          setShowDashboardDatePicker(false);
                        } else {
                          setDashboardPickerValue({ from: range.from, to: range.to });
                        }
                      }}
                      onTodayClick={() => {
                        const today = new Date();
                        const to = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                        dashboardFilter.setRange(to, to);
                        setShowDashboardDatePicker(false);
                      }}
                      onResetClick={() => {
                        const now = new Date();
                        const to = now.toISOString().slice(0, 10);
                        const from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
                        dashboardFilter.setRange(from, to);
                        setShowDashboardDatePicker(false);
                      }}
                    />
                  </div>
                )}
              </div>
            )}
            {showTransactionsFilters && txFilters && (
              <div className="relative">
                <button
                  data-tx-date-picker-button
                  onClick={() => setShowTxDatePicker(!showTxDatePicker)}
                  className={`flex items-center justify-center w-10 h-10 rounded-lg transition-colors duration-200 ${
                    txFilters.filters.dateRange?.from && txFilters.filters.dateRange?.to
                      ? 'bg-blue-500 text-white'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                  title="Фильтр по дате"
                >
                  <FiCalendar className="w-4 h-4" />
                </button>
                {showTxDatePicker && (
                  <div data-tx-date-range-picker className="absolute right-0 top-12 z-50">
                    <DateRangePicker
                      value={txFilters.filters.dateRange || { from: null, to: null }}
                      onChange={(range) => {
                        txFilters.setDateRange(range);
                        if (range.from && range.to) setShowTxDatePicker(false);
                      }}
                      onTodayClick={() => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const todayEnd = new Date(today);
                        todayEnd.setHours(23, 59, 59, 999);
                        txFilters.setDateRange({ from: today, to: todayEnd });
                        setShowTxDatePicker(false);
                      }}
                      onResetClick={() => {
                        const today = new Date();
                        today.setHours(23, 59, 59, 999);
                        const yesterday = new Date(today);
                        yesterday.setDate(yesterday.getDate() - 1);
                        yesterday.setHours(0, 0, 0, 0);
                        txFilters.setDateRange({ from: yesterday, to: today });
                        setShowTxDatePicker(false);
                      }}
                    />
                  </div>
                )}
              </div>
            )}
            {showNewTransactionButton && onNewTransaction && (
              <button
                onClick={onNewTransaction}
                className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-lg hover:from-green-600 hover:to-blue-600 transition-colors shadow-sm"
              >
                <FiDollarSign className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="text-xs sm:text-sm font-medium hidden sm:inline">Новая транзакция</span>
              </button>
            )}
            {showAddTeamMemberButton && onAddTeamMember && (
              <button
                onClick={onAddTeamMember}
                className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:from-purple-600 hover:to-blue-600 transition-colors shadow-sm"
              >
                <FiPlus className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="text-xs sm:text-sm font-medium hidden sm:inline">Добавить участника</span>
              </button>
            )}
            {showAirexRequestsFilters && showWithdrawButton && onWithdraw && (
              <button
                onClick={onWithdraw}
                className="w-full md:w-auto flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-600 hover:to-orange-600 transition-colors shadow-sm"
                title="Создать задачу на вывод"
              >
                <FiDollarSign className="w-4 h-4" />
                <span className="text-sm font-medium">Вывод</span>
              </button>
            )}
            {showAirexRequestsFilters && (
            <>
            <div className="relative w-full md:w-auto">
              <button
                data-airex-pick-widget-button
                onClick={() => {
                  if (pickWidgetOpen) resetMatchCarousel();
                  setPickDropdownOpen(false);
                  setPickFilterTab(pickBankName ? 'bank' : 'direction');
                  setPickWidgetOpen((v) => !v);
                }}
                className="w-full md:w-auto flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg hover:from-blue-600 hover:to-indigo-600 transition-colors shadow-sm"
                title="Открыть виджет подбора"
              >
                <FiSearch className="w-4 h-4" />
                <span className="text-sm font-medium">Подобрать</span>
              </button>
              {pickWidgetOpen && (
                <>
                <button
                  type="button"
                  className="fixed inset-0 z-[199] bg-black/55 md:hidden"
                  aria-label="Закрыть подбор"
                  onClick={() => {
                    setPickWidgetOpen(false);
                    setPickDropdownOpen(false);
                  }}
                />
                <div
                  id="airex-pick-widget-cp"
                  className="max-md:fixed max-md:inset-x-0 max-md:bottom-0 max-md:top-auto max-md:z-[200] max-md:mt-0 max-md:max-h-[min(92dvh,900px)] max-md:overflow-y-auto max-md:overscroll-y-contain max-md:rounded-t-2xl max-md:border-x max-md:border-t max-md:border-gray-600 max-md:bg-gray-800 max-md:shadow-2xl md:absolute md:left-auto md:right-0 md:top-full md:z-50 md:mt-2 md:max-h-none md:overflow-visible md:rounded-xl md:border md:border-gray-700 md:bg-gray-800 md:shadow-xl md:w-[min(36rem,calc(100vw-1.5rem))] md:max-w-[min(36rem,calc(100vw-1.5rem))]"
                >
                  <div className="flex items-center justify-between gap-3 border-b border-gray-700 px-4 py-3 md:hidden">
                    <span className="text-base font-semibold text-white">Подобрать заявку</span>
                    <button
                      type="button"
                      className="rounded-lg p-2 text-gray-300 transition-colors hover:bg-white/10 hover:text-white"
                      aria-label="Закрыть"
                      onClick={() => {
                        setPickWidgetOpen(false);
                        setPickDropdownOpen(false);
                      }}
                    >
                      <FiX className="h-5 w-5" aria-hidden />
                    </button>
                  </div>
                  <div className="p-4 sm:p-5 max-md:px-4 max-md:pb-[max(1.25rem,env(safe-area-inset-bottom))] max-md:pt-3">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                      <div className="min-w-0 flex flex-col gap-1.5">
                        <span className="flex min-h-[2.5rem] items-end text-xs leading-tight text-gray-300">Сумма мэтча (₽)</span>
                        <input
                          value={pickAmount}
                          onChange={(e) => { setPickAmount(e.target.value); resetMatchCarousel(); }}
                          placeholder="Напр. 10000"
                          className="h-10 w-full rounded-lg border-0 bg-white/10 px-3 text-sm text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/40"
                          disabled={isSearchingMatches}
                          inputMode="decimal"
                        />
                      </div>
                      <div className="min-w-0 flex flex-col gap-1.5">
                        <span className="flex min-h-[2.5rem] items-end text-xs leading-tight text-gray-300">Направление / банк</span>
                        <div className="relative" data-pick-target-dropdown>
                          <button
                            type="button"
                            onClick={() => {
                              if (isSearchingMatches) return;
                              setPickFilterTab(pickBankName ? 'bank' : 'direction');
                              setPickDropdownOpen((v) => !v);
                            }}
                            className="flex h-10 w-full items-center justify-between rounded-lg border-0 bg-white/10 px-3 text-left text-sm text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white/40 disabled:opacity-60"
                            disabled={isSearchingMatches}
                          >
                            <span className="min-w-0 truncate">{selectedPickTargetLabel}</span>
                            <FiChevronDown className={`ml-2 h-4 w-4 shrink-0 text-white/70 transition-transform ${pickDropdownOpen ? 'rotate-180' : ''}`} />
                          </button>
                          {pickDropdownOpen && (
                            <div className="absolute left-0 right-0 z-20 mt-1 max-h-56 overflow-y-auto rounded-lg border border-gray-600 bg-gray-900 shadow-xl">
                              <div className="grid grid-cols-2 gap-1 p-1 border-b border-gray-700 bg-gray-900/90 sticky top-0">
                                <button
                                  type="button"
                                  onClick={() => setPickFilterTab('direction')}
                                  className={`px-2 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                    pickFilterTab === 'direction' ? 'bg-indigo-500/30 text-indigo-100' : 'text-gray-300 hover:bg-white/10'
                                  }`}
                                >
                                  Направл.
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setPickFilterTab('bank')}
                                  className={`px-2 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                    pickFilterTab === 'bank' ? 'bg-indigo-500/30 text-indigo-100' : 'text-gray-300 hover:bg-white/10'
                                  }`}
                                >
                                  Банк
                                </button>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  setPickDirection('');
                                  setPickBankName('');
                                  resetMatchCarousel();
                                  setPickDropdownOpen(false);
                                }}
                                className={`w-full px-3 py-2 text-sm text-left hover:bg-white/10 transition-colors ${
                                  pickDirection === '' && pickBankName === '' ? 'bg-indigo-500/20 text-indigo-200' : 'text-gray-100'
                                }`}
                              >
                                Любое
                              </button>
                              {pickFilterTab === 'direction' ? (
                                pickDirectionOptions.map((dir) => (
                                  <button
                                    key={dir.value}
                                    type="button"
                                    onClick={() => {
                                      setPickDirection(dir.value);
                                      setPickBankName('');
                                      resetMatchCarousel();
                                      setPickDropdownOpen(false);
                                    }}
                                    className={`w-full px-3 py-2 text-sm text-left hover:bg-white/10 transition-colors ${
                                      pickDirection === dir.value ? 'bg-indigo-500/20 text-indigo-200' : 'text-gray-100'
                                    }`}
                                  >
                                    {dir.label}
                                  </button>
                                ))
                              ) : (
                                <>
                                  {pickBankOptions.length > 0 ? (
                                    pickBankOptions.map((bankName) => (
                                      <button
                                        key={bankName}
                                        type="button"
                                        onClick={() => {
                                          setPickBankName(bankName);
                                          setPickDirection('');
                                          resetMatchCarousel();
                                          setPickDropdownOpen(false);
                                        }}
                                        className={`w-full px-3 py-2 text-sm text-left hover:bg-white/10 transition-colors ${
                                          pickBankName === bankName ? 'bg-indigo-500/20 text-indigo-200' : 'text-gray-100'
                                        }`}
                                      >
                                        {bankName}
                                      </button>
                                    ))
                                  ) : (
                                    <div className="px-3 py-2 text-sm text-gray-500">Нет банков</div>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="min-w-0 flex flex-col gap-1.5">
                        <div className="flex min-h-[2.5rem] items-end justify-between gap-1.5">
                          <span className="text-xs leading-tight text-gray-300">Выдержка (сек)</span>
                          <span className="group relative inline-flex shrink-0">
                            <button
                              type="button"
                              tabIndex={0}
                              className="rounded p-0.5 text-gray-400 outline-none transition-colors hover:text-white focus-visible:ring-2 focus-visible:ring-white/40"
                              aria-label="Как работает выдержка"
                            >
                              <FiInfo className="h-3.5 w-3.5" aria-hidden />
                            </button>
                            <span
                              role="tooltip"
                              className="pointer-events-none invisible absolute top-full right-0 z-[100] mt-1.5 w-[min(14rem,calc(100vw-2rem))] rounded-lg border border-gray-600 bg-gray-950 px-2.5 py-2 text-[11px] font-normal leading-snug text-gray-100 opacity-0 shadow-xl transition-[opacity,visibility] duration-150 group-hover:pointer-events-auto group-hover:visible group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:visible group-focus-within:opacity-100"
                            >
                              <span className="whitespace-pre-line text-left">{HOLD_SECONDS_HELP}</span>
                            </span>
                          </span>
                        </div>
                        <input
                          value={pickTimeoutSeconds}
                          onChange={(e) => setPickTimeoutSeconds(e.target.value)}
                          placeholder="0"
                          className="h-10 w-full rounded-lg border-0 bg-white/10 px-3 text-sm text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/40"
                          disabled={isSearchingMatches}
                          inputMode="numeric"
                        />
                      </div>
                    </div>

                    <div className="mt-4 border-t border-gray-700/60 pt-4">
                      <button
                        type="button"
                        onClick={async () => {
                          const amount = Number(String(pickAmount).replace(',', '.'));
                          if (!Number.isFinite(amount) || amount <= 0) {
                            window.dispatchEvent(new CustomEvent('showToast', { detail: { message: 'Ошибка: некорректная сумма', type: 'error' } }));
                            return;
                          }
                          if (cpSettings.max_amount_limit != null && amount > cpSettings.max_amount_limit) {
                            window.dispatchEvent(
                              new CustomEvent('showToast', {
                                detail: {
                                  message: `Сумма превышает лимит ${cpSettings.max_amount_limit.toLocaleString('ru-RU')} ₽`,
                                  type: 'error',
                                },
                              })
                            );
                            return;
                          }
                          const holdSec = Math.max(0, Math.min(300, parseInt(pickTimeoutSeconds || '0', 10) || 0));
                          const searchGen = ++pickSearchGenRef.current;
                          const pollMs = 2000;
                          const start = Date.now();
                          const endBy = holdSec > 0 ? start + holdSec * 1000 : start;
                          setIsSearchingMatches(true);
                          try {
                            let list: MatchCandidate[] = [];
                            do {
                              if (pickSearchGenRef.current !== searchGen) return;
                              list = await cpApi.postMatchCandidates({
                                amount,
                                direction_code: pickDirection || undefined,
                                bank_name: pickBankName || undefined,
                                limit: 40,
                              });
                              if (list.length > 0) break;
                              if (holdSec === 0 || Date.now() >= endBy) break;
                              const wait = Math.min(pollMs, Math.max(0, endBy - Date.now()));
                              if (wait <= 0) break;
                              await new Promise((resolve) => setTimeout(resolve, wait));
                            } while (holdSec > 0 && Date.now() < endBy);
                            if (pickSearchGenRef.current !== searchGen) return;
                            setMatchCandidates(list);
                            setCarouselIdx(0);
                            if (!list.length) {
                              window.dispatchEvent(
                                new CustomEvent('showToast', {
                                  detail: {
                                    message: holdSec > 0
                                      ? 'За время выдержки подходящих заявок не появилось'
                                      : 'Нет заявок с достаточным остатком под эту сумму',
                                    type: 'error',
                                  },
                                })
                              );
                            }
                          } catch (e: unknown) {
                            if (pickSearchGenRef.current !== searchGen) return;
                            const err = e as { response?: { data?: { error?: string } }; message?: string };
                            const msg = err?.response?.data?.error || err?.message || 'Ошибка запроса';
                            window.dispatchEvent(new CustomEvent('showToast', { detail: { message: msg, type: 'error' } }));
                            resetMatchCarousel();
                          } finally {
                            if (pickSearchGenRef.current === searchGen) {
                              setIsSearchingMatches(false);
                            }
                          }
                        }}
                        className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-green-500 to-blue-500 px-4 text-sm font-medium text-white shadow-sm transition-colors hover:from-green-600 hover:to-blue-600 disabled:opacity-60"
                        disabled={isSearchingMatches}
                        title="Найти заявки, куда поместится сумма мэтча"
                      >
                        {isSearchingMatches ? (
                          <span className="flex items-center gap-1">
                            <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                            ищу
                          </span>
                        ) : (
                          <>
                            <FiSearch className="w-4 h-4" />
                            <span className="text-sm font-medium">Найти заявки</span>
                          </>
                        )}
                      </button>
                    </div>
                    {matchCandidates.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-600">
                        <div className="text-xs text-gray-300 mb-3 flex justify-between gap-2">
                          <span>Кандидаты ({matchCandidates.length})</span>
                          <span className="text-gray-500">
                            {carouselIdx + 1} / {matchCandidates.length}
                          </span>
                        </div>
                        {(() => {
                          const r = matchCandidates[carouselIdx];
                          if (!r) return null;
                          const isC2C = r.direction_code === 'tbank_c2c' || r.direction_code === 'interbank_c2c';
                          const isSBP = r.direction_code === 'tbank_sbp' || r.direction_code === 'interbank_sbp';
                          return (
                            <div className="flex items-stretch gap-2">
                              <button
                                type="button"
                                aria-label="Предыдущая"
                                onClick={() => setCarouselIdx((i) => Math.max(0, i - 1))}
                                disabled={carouselIdx <= 0 || isSearchingMatches}
                                className="shrink-0 w-9 flex items-center justify-center rounded-lg bg-white/10 text-white hover:bg-white/20 disabled:opacity-40"
                              >
                                <FiChevronLeft className="w-5 h-5" />
                              </button>
                              <div className="flex-1 min-w-0 px-3 py-3 bg-white/5 rounded-lg text-sm">
                                <div className="font-semibold text-white max-md:break-all max-md:text-[13px] max-md:leading-snug md:truncate">
                                  #{r.order_id}
                                </div>
                                <div className="text-gray-300 mt-1">
                                  Остаток{' '}
                                  <span className="text-white font-medium">
                                    {r.remaining_rub.toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₽
                                  </span>
                                </div>
                                {r.bank_name ? (
                                  <div className="mt-1 text-gray-400 max-md:break-words md:truncate">{r.bank_name}</div>
                                ) : null}
                                <div className="mt-2 flex items-center gap-1 text-gray-400">
                                  {isC2C && <CreditCard className="w-3.5 h-3.5 text-blue-400" />}
                                  {isSBP && <Phone className="w-3.5 h-3.5 text-blue-400" />}
                                </div>
                              </div>
                              <button
                                type="button"
                                aria-label="Следующая"
                                onClick={() => setCarouselIdx((i) => Math.min(matchCandidates.length - 1, i + 1))}
                                disabled={carouselIdx >= matchCandidates.length - 1 || isSearchingMatches}
                                className="shrink-0 w-9 flex items-center justify-center rounded-lg bg-white/10 text-white hover:bg-white/20 disabled:opacity-40"
                              >
                                <FiChevronRight className="w-5 h-5" />
                              </button>
                            </div>
                          );
                        })()}
                        <button
                          type="button"
                          onClick={() => {
                            const r = matchCandidates[carouselIdx];
                            if (!r) return;
                            const amt = Number(String(pickAmount).replace(',', '.'));
                            const match_amount = Number.isFinite(amt) && amt > 0 ? amt : undefined;
                            window.dispatchEvent(
                              new CustomEvent('openConfirmTake', {
                                detail: { request: matchCandidateToTakeRequest(r), match_amount },
                              })
                            );
                            setPickWidgetOpen(false);
                            resetMatchCarousel();
                          }}
                          className="w-full mt-3 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg"
                        >
                          Взять эту заявку
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                </>
              )}
            </div>
            <div className="col-span-2 md:col-auto flex flex-row md:flex-col items-center justify-between md:justify-center gap-2">
              {!isSettingsPage && (
                <div className="text-xs font-mono text-gray-300 bg-gray-800/50 px-1.5 py-0.5 rounded mb-1">{moscowTime}</div>
              )}
              <div className="relative">
                <button
                  data-date-picker-button
                  onClick={() => setShowDatePicker(!showDatePicker)}
                  className="px-2 py-1 bg-gray-600/50 hover:bg-gray-600 text-gray-100 text-xs font-medium rounded border border-gray-500/30"
                >
                  {(() => {
                    if (!dateRange?.from || !dateRange?.to) return format(new Date(), 'dd.MM.yyyy');
                    const today = startOfDay(new Date());
                    const yesterday = new Date(today);
                    yesterday.setDate(yesterday.getDate() - 1);
                    const fromDay = startOfDay(dateRange.from);
                    const toDay = startOfDay(dateRange.to);
                    if (fromDay.getTime() === yesterday.getTime() && toDay.getTime() === today.getTime()) {
                      return format(dateRange.to, 'dd.MM.yyyy');
                    }
                    return `${format(dateRange.from, 'dd.MM.yyyy')} - ${format(dateRange.to, 'dd.MM.yyyy')}`;
                  })()}
                  <svg className="ml-2 -mr-1 h-4 w-4 inline" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                </button>
                {showDatePicker && (
                  <div data-date-range-picker className="absolute right-0 mt-2 z-50">
                    <DateRangePicker
                      value={dateRange || { from: null, to: null }}
                      onChange={(range) => {
                        onDateRangeChange?.(range);
                        if (range.from && range.to) setShowDatePicker(false);
                        window.dispatchEvent(new CustomEvent('airexDateRangeChange', { detail: { range } }));
                      }}
                      onTodayClick={() => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const todayEnd = new Date(today);
                        todayEnd.setHours(23, 59, 59, 999);
                        const range = { from: today, to: todayEnd };
                        onDateRangeChange?.(range);
                        setShowDatePicker(false);
                        window.dispatchEvent(new CustomEvent('airexDateRangeChange', { detail: { range } }));
                      }}
                      onResetClick={() => {
                        const today = new Date();
                        today.setHours(23, 59, 59, 999);
                        const yesterday = new Date(today);
                        yesterday.setDate(yesterday.getDate() - 1);
                        yesterday.setHours(0, 0, 0, 0);
                        const range = { from: yesterday, to: today };
                        onDateRangeChange?.(range);
                        setShowDatePicker(false);
                        window.dispatchEvent(new CustomEvent('airexDateRangeChange', { detail: { range } }));
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={() => {
                onRefresh?.();
                window.dispatchEvent(new CustomEvent('refreshRequests'));
              }}
              className="col-span-2 md:col-auto w-full md:w-auto px-3 py-1.5 bg-gray-600/50 hover:bg-gray-600 text-gray-100 text-sm font-medium rounded-md border border-gray-500/30 flex items-center justify-center"
              title={isTableUpdating ? 'Обновление…' : 'Обновить'}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${isTableUpdating ? 'animate-spin animate-pulse text-sky-400' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            </>
            )}
          </div>
        )}

      </div>
    </header>
  );
}
